import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CATEGORIES } from "../../lib/products";
import { Rise } from "./Reveal";
import { useLang } from "../../lib/LangContext";
import { localizedCategory, translateProduct } from "../../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CategoryBlock({ cat, isLast }) {
  const ref = useRef(null);
  const { dict, lang } = useLang();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const loc = localizedCategory(dict, cat.id);

  return (
    <section
      ref={ref}
      id={cat.id}
      data-testid={`category-${cat.id}`}
      className={`relative ${isLast ? "" : "border-b hairline"}`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid grid-cols-12 gap-6 md:gap-10 items-start">
        <aside className="col-span-12 md:col-span-4 lg:col-span-3 md:sticky md:top-28 self-start">
          <div className="flex items-center gap-4 mb-8">
            <span className="serif italic text-6xl md:text-7xl text-[#B56B45] leading-none">
              {cat.index}
            </span>
            <span className="label text-[#1A1918]">{dict.chapter_kicker(cat.index)}</span>
          </div>
          <h3 className="serif font-light text-5xl md:text-6xl leading-[0.95] tracking-[-0.02em] text-[#1A1918]">
            {loc.title}
          </h3>
          <p className="serif italic text-xl md:text-2xl mt-4 text-[#5C5A55]">
            {loc.subtitle}
          </p>
          <p className="mt-8 text-[15px] leading-[1.7] text-[#5C5A55] max-w-[42ch]">
            {loc.blurb}
          </p>
          <div className="mt-10 flex items-center gap-6 label text-[10px] text-[#1A1918]">
            <span>{dict.catalogue_products_count(cat.items.length)}</span>
            <span aria-hidden>·</span>
            <a href="#contact" className="link-under" data-testid={`category-${cat.id}-quote`}>
              {dict.catalogue_request_quote}
            </a>
          </div>
        </aside>

        <div className="col-span-12 md:col-span-8 lg:col-span-9 flex flex-col gap-14">
          <div className="relative w-full overflow-hidden">
            <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden">
              <motion.img
                src={cat.image}
                alt={`${loc.title} products`}
                style={{ y: imgY }}
                className="absolute inset-0 w-full h-[115%] object-cover spot"
                loading="lazy"
              />
              <div className="absolute top-4 left-4 label text-[10px] text-[#F7F5F0] mix-blend-difference">
                {dict.catalogue_plate(cat.index)}
              </div>
              <div className="absolute bottom-4 right-4 label text-[10px] text-[#F7F5F0] mix-blend-difference">
                {dict.catalogue_studio_still(loc.title)}
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="grid grid-cols-12 gap-4 pb-4 border-b hairline">
              <div className="col-span-2 label text-[10px] text-[#1A1918]">
                {dict.catalogue_ref}
              </div>
              <div className="col-span-10 label text-[10px] text-[#1A1918]">
                {dict.catalogue_product}
              </div>
            </div>

            {/* Product image grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-14 pt-8 md:pt-12">
              {cat.items.map((it, i) => (
                <Rise key={`${cat.id}-${i}`} delay={(i % 6) * 0.04} y={20}>
                  <article
                    data-testid={`product-${cat.id}-${i}`}
                    className="group cursor-default"
                  >
                    <div className="relative w-full aspect-[4/5] overflow-hidden bg-[#EAE6DF]">
                      <img
                        src={it.img}
                        alt={translateProduct(dict, it.name, lang)}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover spot transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                      />
                      <div className="absolute top-3 left-3 label text-[9px] text-[#F7F5F0] mix-blend-difference">
                        {cat.index}·{it.n}
                      </div>
                    </div>
                    <div className="mt-4 flex items-baseline gap-3">
                      <span className="serif italic text-2xl md:text-3xl text-[#B56B45] leading-none">
                        {it.n}
                      </span>
                      <h4 className="serif text-lg md:text-xl font-light tracking-tight text-[#1A1918] leading-[1.2]">
                        {translateProduct(dict, it.name, lang)}
                      </h4>
                    </div>
                  </article>
                </Rise>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Categories() {
  const { dict } = useLang();
  return (
    <div
      id="catalogue"
      data-testid="catalogue-section"
      className="relative bg-[#F7F5F0] border-t hairline"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-8 md:pb-16">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-3">
            <span className="label text-[#1A1918]">{dict.catalogue_kicker}</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.02em] max-w-[24ch] text-[#1A1918]">
              {dict.catalogue_title_1}
              <br />
              <span className="italic text-[#5C5A55]">{dict.catalogue_title_2}</span>
            </h2>
            <a
              href={`${API}/catalogue.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="catalogue-download-pdf"
              className="mt-10 inline-flex items-center gap-3 px-5 py-3 border border-[#1A1918] text-[#1A1918] label text-[10px] hover:bg-[#1A1918] hover:text-[#F7F5F0] transition-colors duration-500"
            >
              {dict.catalogue_download}
              <span aria-hidden>↓</span>
            </a>
          </div>
        </div>
      </div>

      {CATEGORIES.map((cat, i) => (
        <CategoryBlock cat={cat} key={cat.id} isLast={i === CATEGORIES.length - 1} />
      ))}
    </div>
  );
}
